import {
    IonBackButton,
    IonButton,
    IonButtons,
    IonCard,
    IonCardContent,
    IonContent,
    IonHeader,
    IonIcon,
    IonPage,
    IonTitle,
    IonToolbar,
    useIonRouter,
    IonItem,
    IonInput,
    IonLabel,
    IonAvatar,
    IonToast
} from '@ionic/react';
import {
    personOutline,
    statsChartOutline,
    logOutOutline,
    createOutline,
    saveOutline,
    closeOutline,
    cameraOutline
} from 'ionicons/icons';
import { useState, useEffect } from 'react';
import axios from 'axios';
import API_CONFIG from '../config/api';
import './View.css';
import Loader from '../components/Loader';
import LocationSelector from '../components/LocationSelector';

const UserDashboard = () => {
    const router = useIonRouter();
    const [user, setUser] = useState({
        name: '',
        email: '',
        role: '',
        avatar: '',
        joinDate: '',
        votesCast: 0,
        country: '',
        state: '',
        local_government: ''
    });
    const [isEditing, setIsEditing] = useState(false);
    const [editForm, setEditForm] = useState({
        name: '',
        email: '',
        country: '',
        state: '',
        local_government: ''
    });
    const [loading, setLoading] = useState(false);
    const [toast, setToast] = useState({ show: false, message: '', color: 'success' });

    useEffect(() => {
        if (!sessionStorage.getItem('user_id')) {
            router.push('/page/login');
            return;
        }
        fetchUserData();
    }, []);

    const fetchUserData = async () => {
        try {
            const userId = localStorage.getItem('userId');
            // Assuming we have an endpoint to get single user details, or we repurpose manage_users with ID
            // For now, using manage_users with specific ID filter if backend supports, otherwise rely on stored info + fresh fetch

            // In a real app, you'd have a specific /profile endpoint. 
            // I'll try to fetch from manage_users filtered by ID for now as per existing patterns
            const response = await axios.get(`${API_CONFIG.MANAGE_USERS}?user_id=${userId}&id=${userId}`);

            if (response.data.success && response.data.users && response.data.users.length > 0) {
                const userData = response.data.users[0];
                const userObj = {
                    name: userData.name || `${userData.first_name} ${userData.last_name}`,
                    email: userData.email,
                    role: getUserRole(userData.role_id),
                    avatar: userData.avatar_url, // Or generate based on name
                    joinDate: userData.created_at,
                    votesCast: userData.votes_count || 0,
                    country: userData.country || '',
                    state: userData.state || '',
                    local_government: userData.local_government || ''
                };
                setUser(userObj);
                setEditForm({
                    name: userObj.name,
                    email: userObj.email,
                    country: userObj.country,
                    state: userObj.state,
                    local_government: userObj.local_government
                });
            }
        } catch (error) {
            console.error('Error fetching user data:', error);
            setToast({ show: true, message: 'Failed to load profile data', color: 'danger' });
        }
    };

    const getUserRole = (roleId) => {
        const roles = {
            1: 'Super Admin',
            2: 'Organization Admin',
            3: 'Moderator',
            4: 'Member',
            5: 'Viewer'
        };
        return roles[roleId] || 'User';
    };

    const handleUpdateProfile = async () => {
        setLoading(true);
        try {
            const userId = localStorage.getItem('userId');
            const nameParts = editForm.name.split(' ');
            const firstName = nameParts[0];
            const lastName = nameParts.slice(1).join(' ') || '';

            const payload = {
                user_id: userId,
                id: userId,
                name: editForm.name,
                first_name: firstName,
                last_name: lastName,
                country: editForm.country,
                state: editForm.state,
                local_government: editForm.local_government
            };

            // Only include email if it was actually changed
            if (editForm.email !== user.email) {
                payload.email = editForm.email;
            }

            const response = await axios.put(API_CONFIG.MANAGE_USERS, payload);

            if (response.data.success) {
                setUser(prev => ({ 
                    ...prev, 
                    name: editForm.name, 
                    email: editForm.email,
                    country: editForm.country,
                    state: editForm.state,
                    local_government: editForm.local_government 
                }));
                setIsEditing(false);
                setToast({ show: true, message: 'Profile updated successfully', color: 'success' });
            } else {
                setToast({ show: true, message: response.data.error || 'Update failed', color: 'danger' });
            }
        } catch (error) {
            console.error('Error updating profile:', error);
            const errorMsg = error.response?.data?.error || 'Failed to update profile';
            setToast({ show: true, message: errorMsg, color: 'danger' });
        } finally {
            setLoading(false);
        }
    };

    const handleLogout = () => {
        sessionStorage.clear();
        localStorage.clear();
        window.location.href = '/page/login';
    };

    return (
        <IonPage className="view-page">
            <IonHeader className="ion-no-border" translucent={true}>
                <IonToolbar className="header-toolbar frosted-header">
                    <IonButtons slot="start">
                        <IonBackButton text="Back" className="custom-back-button" defaultHref="/page/view" />
                    </IonButtons>
                    <IonTitle className="page-title">
                        <span className="title-text">My Dashboard</span>
                    </IonTitle>
                    <IonButtons slot="end">
                        <IonButton color="danger" onClick={handleLogout}>
                            <IonIcon icon={logOutOutline} slot="icon-only" />
                        </IonButton>
                    </IonButtons>
                </IonToolbar>
            </IonHeader>

            <IonContent fullscreen className="view-content">
                {loading && <Loader message="Synchronizing Personal Metrics..." />}
                <div className="max-w-4xl mx-auto p-4 space-y-6">

                    {/* Profile Card */}
                    <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
                        <div className="bg-gradient-to-r from-blue-500 to-indigo-600 h-32"></div>
                        <div className="px-6 pb-6 relative">
                            <div className="flex justify-between items-end -mt-12 mb-4">
                                <div className="relative">
                                    <div className="w-24 h-24 bg-white rounded-full p-1 shadow-lg">
                                        <div className="w-full h-full bg-gray-100 rounded-full flex items-center justify-center text-3xl">
                                            {user.avatar ? <img src={user.avatar} alt="Profile" className="w-full h-full rounded-full object-cover" /> : '👤'}
                                        </div>
                                    </div>
                                </div>
                                {!isEditing && (
                                    <IonButton fill="outline" shape="round" onClick={() => setIsEditing(true)}>
                                        <IonIcon icon={createOutline} slot="start" />
                                        Edit Profile
                                    </IonButton>
                                )}
                            </div>

                            {isEditing ? (
                                <div className="space-y-4 animate-fade-in">
                                    <IonItem lines="full" className="rounded-lg border border-gray-200">
                                        <IonLabel position="stacked">Full Name</IonLabel>
                                        <IonInput
                                            value={editForm.name}
                                            onIonChange={e => setEditForm(prev => ({ ...prev, name: e.detail.value }))}
                                        />
                                    </IonItem>
                                    <IonItem lines="full" className="rounded-lg border border-gray-200">
                                        <IonLabel position="stacked">Email</IonLabel>
                                        <IonInput
                                            value={editForm.email}
                                            onIonChange={e => setEditForm(prev => ({ ...prev, email: e.detail.value }))}
                                        />
                                    </IonItem>
                                    <div className="pt-2 px-1">
                                        <LocationSelector 
                                            country={editForm.country}
                                            state={editForm.state}
                                            localGovernment={editForm.local_government}
                                            onChange={(field, value) => {
                                                const mapping = { localGovernment: 'local_government' };
                                                const finalField = mapping[field] || field;
                                                setEditForm(prev => ({ ...prev, [finalField]: value }));
                                            }}
                                            required={true}
                                        />
                                    </div>
                                    <div className="flex gap-3 pt-2">
                                        <IonButton expand="block" onClick={handleUpdateProfile} disabled={loading} className="flex-1">
                                            <IonIcon icon={saveOutline} slot="start" />
                                            {loading ? 'Saving...' : 'Save Changes'}
                                        </IonButton>
                                        <IonButton expand="block" color="light" onClick={() => setIsEditing(false)} className="flex-1">
                                            <IonIcon icon={closeOutline} slot="start" />
                                            Cancel
                                        </IonButton>
                                    </div>
                                </div>
                            ) : (
                                <div>
                                    <h2 className="text-2xl font-bold text-gray-800">{user.name}</h2>
                                    <p className="text-gray-500 font-medium">{user.role}</p>
                                    <div className="flex items-center gap-2 mt-2 text-sm text-gray-500">
                                        <IonIcon icon={personOutline} />
                                        <span>{user.email}</span>
                                    </div>
                                    {user.country && (
                                        <div className="flex items-center gap-2 mt-1 text-sm text-gray-500">
                                            <span>📍</span>
                                            <span>
                                                {[user.local_government, user.state, user.country].filter(Boolean).join(', ')}
                                            </span>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Stats / Activity */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="bg-white rounded-xl shadow-md p-6 border-l-4 border-blue-500">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm font-medium text-gray-500">Total Votes Cast</p>
                                    <p className="text-3xl font-bold text-gray-800">{user.votesCast}</p>
                                </div>
                                <div className="bg-blue-50 p-3 rounded-full">
                                    <IonIcon icon={statsChartOutline} className="text-xl text-blue-500" />
                                </div>
                            </div>
                        </div>

                        <div className="bg-white rounded-xl shadow-md p-6 border-l-4 border-green-500 cursor-pointer hover:bg-gray-50 transition-colors"
                            onClick={() => router.push('/page/view')}>
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm font-medium text-gray-500">Active Polls</p>
                                    <p className="text-lg font-bold text-green-600">Browse Now</p>
                                </div>
                                <div className="bg-green-50 p-3 rounded-full">
                                    <IonIcon icon={statsChartOutline} className="text-xl text-green-500" />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

            </IonContent>
            <IonToast
                isOpen={toast.show && !!toast.message}
                onDidDismiss={() => setToast({ ...toast, show: false })}
                message={toast.message}
                duration={3000}
                color={toast.color}
                position="top"
            />
        </IonPage>

    );
};

export default UserDashboard;
